/**
 * A barra fina no topo: "o sistema está trabalhando".
 *
 * Responde à pergunta que uma espera silenciosa deixa em aberto — *travou ou está processando?* —
 * e responde para o produto inteiro de uma vez, porque lê o contador de requisições do cliente HTTP
 * (`lib/emAndamento`) em vez de depender de cada tela lembrar de sinalizar.
 *
 * Três decisões, e as três são sobre não mentir:
 *
 * **Só aparece depois de 220ms.** Requisição rápida não pisca nada. Uma barra que aparece e some em
 * 80ms é ruído: o olho registra o movimento, não a informação, e a interface parece nervosa.
 *
 * **O avanço é assintótico e nunca chega a 100% sozinho.** Não sabemos quanto falta — o servidor
 * não diz —, então a barra caminha cada vez mais devagar em direção a 90% e só completa quando a
 * resposta chega de fato. Uma barra que finge 100% e depois espera é pior que barra nenhuma.
 *
 * **Some com um respiro depois de completar**, para o "cheio" ser visto. Sumir no mesmo quadro em
 * que enche faz o sucesso passar despercebido.
 */
import { useEffect, useRef, useState } from 'react';
import { assinar } from '@/lib/emAndamento';

const ATRASO_MS = 220;
const TETO = 90;

export function BarraProgresso() {
  const [progresso, setProgresso] = useState<number | null>(null);
  const tick = useRef<number | null>(null);
  const atraso = useRef<number | null>(null);
  const saida = useRef<number | null>(null);

  useEffect(() => {
    const limpar = (r: React.RefObject<number | null>) => {
      if (r.current !== null) window.clearTimeout(r.current);
      r.current = null;
    };

    return assinar((quantas) => {
      if (quantas > 0) {
        if (tick.current !== null || atraso.current !== null) return; // já está correndo
        limpar(saida);
        atraso.current = window.setTimeout(() => {
          atraso.current = null;
          setProgresso(8);
          // Cada passo cobre uma fração do que FALTA — daí desacelerar sozinho perto do teto.
          tick.current = window.setInterval(
            () => setProgresso((p) => (p === null ? 8 : p + (TETO - p) * 0.12)),
            240,
          ) as unknown as number;
        }, ATRASO_MS);
        return;
      }
      // Acabou tudo: se a barra nem chegou a aparecer, some sem deixar rastro.
      limpar(atraso);
      if (tick.current !== null) {
        window.clearInterval(tick.current);
        tick.current = null;
        setProgresso(100);
        saida.current = window.setTimeout(() => setProgresso(null), 260);
      } else {
        setProgresso(null);
      }
    });
  }, []);

  if (progresso === null) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[var(--z-toast)] h-0.5"
      role="progressbar"
      aria-label="Carregando"
      aria-busy="true"
    >
      <div
        className="h-full bg-primary shadow-[var(--halo-primary)]"
        style={{
          width: `${progresso}%`,
          transition: 'width var(--dur-slow) var(--ease), opacity var(--dur-fast)',
          opacity: progresso >= 100 ? 0 : 1,
        }}
      />
    </div>
  );
}
